import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ChevronLeft, LogOut, BookOpen, ChevronRight, Phone, CheckCircle, Save } from "lucide-react";
import { colors, fonts, spacing, radius } from "../../theme";
import { useAdminAuth, usePhoneNumber, updatePhoneNumber, formatPhone } from "../../hooks/useSettings";
import { useMenu } from "../../hooks/useMenu";

export default function AdminSettings() {
  const navigate = useNavigate();
  const { isAuthenticated, loaded, logout } = useAdminAuth();
  const { phone } = usePhoneNumber();
  const { menu } = useMenu();
  const [value, setValue] = useState(phone);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setValue(phone); }, [phone]);

  if (!loaded) return <div style={{ minHeight: "100vh", backgroundColor: colors.surface }} />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;

  const save = async () => {
    const cleaned = value.trim();
    if (!/^[\d\s()+.-]{6,20}$/.test(cleaned)) { setError("Numero non valido. Inserisci solo cifre e simboli telefonici."); return; }
    const digitsOnly = cleaned.replace(/\D/g, "");
    if (digitsOnly.length < 6) { setError("Numero troppo corto."); return; }
    setError(null);
    const ok = await updatePhoneNumber(digitsOnly);
    if (ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    else setError("Impossibile salvare. Riprova.");
  };

  const handleLogout = async () => { await logout(); navigate("/info", { replace: true }); };
  const totalItems = menu.reduce((n, c) => n + c.items.length, 0);

  const card = { marginTop: spacing.xl, backgroundColor: colors.surfaceSecondary, borderRadius: radius.lg, border: `0.5px solid ${colors.border}`, padding: spacing.xl };

  return (
    <div className="pm-fade" style={{ minHeight: "100vh", backgroundColor: colors.surface, paddingLeft: spacing.xl, paddingRight: spacing.xl, paddingTop: spacing.lg, paddingBottom: spacing.xxl }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="pm-press" onClick={() => navigate(-1)} style={{ display: "flex", padding: 8 }}>
          <ChevronLeft size={22} color={colors.onSurface} strokeWidth={2} />
        </div>
        <div className="pm-press" onClick={handleLogout} style={{ display: "flex", alignItems: "center", gap: 6, border: `0.5px solid ${colors.border}`, borderRadius: radius.pill, paddingLeft: spacing.md, paddingRight: spacing.md, paddingTop: 6, paddingBottom: 6 }}>
          <LogOut size={14} color={colors.muted} strokeWidth={1.75} />
          <span style={{ color: colors.muted, fontFamily: fonts.body, fontWeight: 500, fontSize: 11, letterSpacing: 0.8, textTransform: "uppercase" }}>Esci</span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: spacing.lg }}>
        <div style={{ fontFamily: fonts.body, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: colors.brandPrimary, fontWeight: 500 }}>Area riservata</div>
        <div style={{ marginTop: 4, fontFamily: fonts.display, fontSize: 30, fontWeight: 700, color: colors.onSurface }}>Impostazioni</div>
        <div style={{ width: 40, height: 1, backgroundColor: colors.brandPrimary, opacity: 0.7, margin: `${spacing.md}px 0` }} />
        <div style={{ fontFamily: fonts.body, fontSize: 13, color: colors.muted, textAlign: "center", letterSpacing: 0.3, paddingLeft: spacing.xl, paddingRight: spacing.xl }}>Aggiorna il numero di telefono e il menu del ristorante.</div>
      </div>

      {/* Menu editor entry */}
      <div className="pm-press" onClick={() => navigate("/admin/menu")} style={{ marginTop: spacing.xl, display: "flex", alignItems: "center", gap: spacing.md, padding: spacing.lg, borderRadius: radius.lg, border: `0.5px solid ${colors.border}`, backgroundColor: colors.surfaceSecondary }}>
        <div style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: colors.surfaceTertiary, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <BookOpen size={20} color={colors.brandPrimary} strokeWidth={1.75} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, color: colors.onSurface }}>Modifica menu</div>
          <div style={{ fontFamily: fonts.body, fontSize: 12, color: colors.muted, marginTop: 2 }}>{menu.length} categorie · {totalItems} piatti</div>
        </div>
        <ChevronRight size={20} color={colors.muted} strokeWidth={1.75} />
      </div>

      {/* Phone card */}
      <div style={card}>
        <div style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 700, color: colors.onSurface }}>Numero di telefono</div>
        <div style={{ fontFamily: fonts.body, fontSize: 12, color: colors.muted, marginTop: 4, letterSpacing: 0.3 }}>Verrà mostrato ai clienti in home e nella pagina Info.</div>
        <label style={{ fontFamily: fonts.body, fontWeight: 500, fontSize: 11, color: colors.muted, textTransform: "uppercase", letterSpacing: 1.2, marginTop: spacing.lg, marginBottom: spacing.sm, display: "block" }}>Numero</label>
        <div style={{ display: "flex", alignItems: "center", gap: spacing.sm, border: `0.5px solid ${colors.border}`, borderRadius: radius.md, backgroundColor: colors.surface, paddingLeft: spacing.md, paddingRight: spacing.md, height: 48 }}>
          <Phone size={16} color={colors.muted} strokeWidth={1.75} />
          <input style={{ flex: 1, color: colors.onSurface, fontFamily: fonts.body, fontSize: 15, background: "transparent", border: "none" }} value={value} onChange={(e) => { setValue(e.target.value); setSaved(false); setError(null); }} inputMode="tel" placeholder="0183754557" />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: spacing.md, paddingTop: spacing.md, borderTop: `0.5px solid ${colors.divider}` }}>
          <span style={{ fontFamily: fonts.body, fontSize: 12, color: colors.muted, letterSpacing: 0.4 }}>Anteprima</span>
          <span style={{ fontFamily: fonts.display, fontSize: 18, fontWeight: 700, color: colors.brandPrimary, letterSpacing: 0.4 }}>{formatPhone(value) || "—"}</span>
        </div>
        {!!error && <div style={{ marginTop: spacing.md, color: colors.errorText, fontFamily: fonts.body, fontWeight: 500, fontSize: 13 }}>{error}</div>}
        {saved && (
          <div style={{ marginTop: spacing.md, display: "flex", alignItems: "center", width: "fit-content", gap: 6, border: `0.5px solid ${colors.success}`, paddingLeft: spacing.md, paddingRight: spacing.md, paddingTop: 4, paddingBottom: 4, borderRadius: radius.pill, backgroundColor: "rgba(94,173,110,0.08)" }}>
            <CheckCircle size={14} color={colors.success} strokeWidth={2} />
            <span style={{ color: colors.success, fontFamily: fonts.body, fontWeight: 500, fontSize: 12, letterSpacing: 0.5 }}>Salvato</span>
          </div>
        )}
        <button className="pm-press" onClick={save} style={{ marginTop: spacing.xl, width: "100%", display: "flex", justifyContent: "center", alignItems: "center", gap: 8, backgroundColor: colors.brandPrimary, border: "none", paddingTop: 14, paddingBottom: 14, borderRadius: radius.md, color: colors.onBrandPrimary, fontFamily: fonts.body, fontWeight: 700, fontSize: 14, letterSpacing: 0.8, textTransform: "uppercase", cursor: "pointer" }}>
          <Save size={16} color={colors.onBrandPrimary} strokeWidth={2} /> Salva modifiche
        </button>
      </div>
    </div>
  );
}
